// Razorpay checkout for pricing plans.
// Endpoints:
//  POST /api/payments/guest/create-order  body { planId, phone, email }   (no login)
//  POST /api/payments/guest/verify        body { razorpay_* }             (no login)
//  POST /api/payments/create-order        body { planId }                 (logged in)
//  POST /api/payments/verify              body { razorpay_* }             (logged in)
//  GET  /api/payments/my                  payment history
//  GET  /api/payments/status              what the user's plans currently allow

import { API_BASE_URL } from './apiConfig';

const PAYMENTS_BASE = `${API_BASE_URL}/payments`;
const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

interface RazorpayResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

interface RazorpayInstance {
  open(): void;
}

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => RazorpayInstance;
  }
}

interface OrderData {
  // true when the server runs with PAYMENT_DEMO_MODE: no real Razorpay window
  demo?: boolean;
  keyId: string;
  orderId: string;
  amount: number;
  currency: string;
  planName: string;
}

export interface PaymentResult {
  planName: string;
  // Guest checkout only: whether the phone number already has an account
  hasAccount?: boolean;
}

export interface PaymentRecord {
  id: number;
  plan_id: number | null;
  plan_name: string;
  amount: string;
  currency: string;
  razorpay_order_id: string;
  razorpay_payment_id: string | null;
  status: 'created' | 'paid' | 'failed';
  failure_reason: string | null;
  created_at: string;
  paid_at: string | null;
}

export interface PlanAccessStatus {
  // true for roles that manage their plans outside this flow (channel partners)
  exempt: boolean;
  hasAccess: boolean;
  planName: string | null;
  periodEndsAt: string | null;
  creditsRemaining: number;
}

export class PaymentCancelledError extends Error {
  constructor() {
    super('Payment cancelled');
  }
}

function authHeaders(): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('authToken');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${PAYMENTS_BASE}/${path}`, { headers: authHeaders(), ...init });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json || json.success === false) {
    throw new Error(json?.message || `Request failed (${res.status})`);
  }
  return json.data as T;
}

const post = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'POST', body: JSON.stringify(body) });

function loadCheckoutScript(): Promise<void> {
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = CHECKOUT_SRC;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Could not load the payment window. Check your connection.'));
    document.body.appendChild(script);
  });
}

// Stand-in for the Razorpay window while the server is in demo mode: a plain
// confirm dialog that "pays" with the fixed demo signature the server accepts.
function openDemoCheckout(order: OrderData): Promise<RazorpayResponse> {
  return new Promise((resolve, reject) => {
    const overlay = document.createElement('div');
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:2000;display:flex;align-items:center;justify-content:center;padding:1rem;background:rgba(15,23,42,.6)';
    const box = document.createElement('div');
    box.style.cssText =
      'width:100%;max-width:380px;padding:1.5rem;border-radius:14px;background:#fff;color:#0f172a;font-family:inherit;text-align:center';

    const badge = document.createElement('div');
    badge.textContent = 'DEMO PAYMENT - no money is charged';
    badge.style.cssText = 'margin-bottom:.75rem;padding:.3rem;border-radius:8px;background:#fef9c3;color:#854d0e;font-size:.8rem;font-weight:700';
    const title = document.createElement('h3');
    title.textContent = order.planName;
    title.style.margin = '0 0 .25rem';
    const amount = document.createElement('p');
    amount.textContent = `₹${(order.amount / 100).toLocaleString('en-IN')}`;
    amount.style.cssText = 'margin:0 0 1rem;font-size:1.6rem;font-weight:700';

    const close = () => overlay.remove();
    const mkBtn = (label: string, primary: boolean, onClick: () => void) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = label;
      b.style.cssText = `width:100%;margin-top:.5rem;padding:.7rem;border-radius:10px;font-size:1rem;font-weight:600;cursor:pointer;border:${
        primary ? 'none' : '1px solid #cbd5e1'
      };background:${primary ? '#1261d6' : '#fff'};color:${primary ? '#fff' : '#0f172a'}`;
      b.onclick = onClick;
      return b;
    };

    box.append(
      badge,
      title,
      amount,
      mkBtn('Pay now (demo)', true, () => {
        close();
        resolve({
          razorpay_order_id: order.orderId,
          razorpay_payment_id: `demo_pay_${Date.now()}`,
          razorpay_signature: 'demo_signature',
        });
      }),
      mkBtn('Cancel', false, () => {
        close();
        reject(new PaymentCancelledError());
      })
    );
    overlay.appendChild(box);
    document.body.appendChild(overlay);
  });
}

// Opens the Razorpay window for an order and resolves once the server has
// verified the payment; rejects with PaymentCancelledError if the user closes it.
function openCheckout(
  order: OrderData,
  verifyPath: string,
  prefill?: { name?: string; email?: string; contact?: string }
): Promise<PaymentResult> {
  const verify = async (response: RazorpayResponse) =>
    post<{ planName: string; hasAccount?: boolean }>(verifyPath, response);

  if (order.demo) return openDemoCheckout(order).then(verify);

  return new Promise((resolve, reject) => {
    const checkout = new window.Razorpay!({
      key: order.keyId,
      order_id: order.orderId,
      amount: order.amount,
      currency: order.currency,
      name: 'Easy E-Auction',
      description: order.planName,
      prefill,
      handler: async (response: RazorpayResponse) => {
        try {
          resolve(await verify(response));
        } catch (err) {
          reject(err);
        }
      },
      modal: { ondismiss: () => reject(new PaymentCancelledError()) },
    });
    checkout.open();
  });
}

class PaymentService {
  async payForPlan(
    planId: number,
    prefill?: { name?: string; email?: string; contact?: string }
  ): Promise<PaymentResult> {
    const order = await post<OrderData>('create-order', { planId });
    if (!order.demo) await loadCheckoutScript();
    return openCheckout(order, 'verify', prefill);
  }

  // For visitors who aren't logged in: identified by phone + email, and the
  // plan is attached to the account that owns that number.
  async payAsGuest(planId: number, phone: string, email: string): Promise<PaymentResult> {
    const order = await post<OrderData>('guest/create-order', { planId, phone, email });
    if (!order.demo) await loadCheckoutScript();
    return openCheckout(order, 'guest/verify', { email, contact: phone });
  }

  getMyPayments(): Promise<PaymentRecord[]> {
    return request<PaymentRecord[]>('my');
  }

  getStatus(): Promise<PlanAccessStatus> {
    return request<PlanAccessStatus>('status');
  }
}

const paymentService = new PaymentService();
export default paymentService;
